import React, { useState } from "react";
import { toast } from "react-toastify";
import { userService } from "../../../services/userService";
import ConfirmModal from "../../common/ConfirmModal/ConfirmModal";
import { validerImageUtilisateur } from "../../../helpers/imageUtilisateurTools";

const NOM_MAX = 60;

/**
 * LS-689 — gestion des tampons multiples d'un utilisateur : dépôt (avec un nom
 * obligatoire), renommage et suppression. Self-service par défaut (utilisateur
 * connecté) ; `targetUuid` bascule vers les routes d'administration (réservées
 * SUPER_ADMIN côté API, cf. GlobalAccess::GERER_IMAGE_UTILISATEUR).
 *
 * La signature reste unique et garde son composant dédié
 * (`ImageUtilisateurUploader`) : LS-689 ne la touche pas.
 *
 * Le dépôt et le renommage renvoient le user complet (`tampons` trié par nom) ;
 * la suppression répond 204, d'où la recomposition locale de la liste avant
 * `onChange` — même contrainte que pour la signature.
 */
const TamponsManager = ({ user, targetUuid, onChange }) => {
  const tampons = user?.tampons || [];

  const [nom, setNom] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const [renommageUuid, setRenommageUuid] = useState(null);
  const [renommageNom, setRenommageNom] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const [suppressionCible, setSuppressionCible] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const nomValide = nom.trim() !== "" && nom.trim().length <= NOM_MAX;

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permet de re-sélectionner le même fichier
    if (!file) return;

    const erreurFichier = validerImageUtilisateur(file);
    if (erreurFichier) {
      toast.error(erreurFichier);
      return;
    }

    setIsUploading(true);
    try {
      const res = targetUuid
        ? await userService.uploadTamponForUser(targetUuid, file, nom.trim())
        : await userService.uploadTampon(file, nom.trim());
      onChange(res.data);
      setNom("");
      toast.success("Tampon déposé.");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Le dépôt du tampon a échoué.",
      );
    } finally {
      setIsUploading(false);
    }
  }

  function ouvrirRenommage(tampon) {
    setRenommageUuid(tampon.uuid);
    setRenommageNom(tampon.nom || "");
  }

  function annulerRenommage() {
    setRenommageUuid(null);
    setRenommageNom("");
  }

  async function validerRenommage() {
    const nouveauNom = renommageNom.trim();
    if (nouveauNom === "" || nouveauNom.length > NOM_MAX || isRenaming) return;

    setIsRenaming(true);
    try {
      const res = targetUuid
        ? await userService.renameTamponForUser(
            targetUuid,
            renommageUuid,
            nouveauNom,
          )
        : await userService.renameTampon(renommageUuid, nouveauNom);
      onChange(res.data);
      annulerRenommage();
      toast.success("Tampon renommé.");
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Le renommage du tampon a échoué.",
      );
    } finally {
      setIsRenaming(false);
    }
  }

  async function handleConfirmDelete() {
    setIsDeleting(true);
    try {
      if (targetUuid) {
        await userService.deleteTamponForUser(targetUuid, suppressionCible.uuid);
      } else {
        await userService.deleteTampon(suppressionCible.uuid);
      }
      onChange({
        ...user,
        tampons: tampons.filter((t) => t.uuid !== suppressionCible.uuid),
      });
      toast.success("Tampon supprimé.");
      setSuppressionCible(null);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "La suppression du tampon a échoué.",
      );
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">Tampons</h5>

        {tampons.length === 0 ? (
          <p className="text-muted">Aucun tampon déposé.</p>
        ) : (
          <ul className="list-unstyled">
            {tampons.map((tampon) => (
              <li
                key={tampon.uuid}
                className="d-flex align-items-center mb-2"
                style={{ gap: 8 }}
              >
                <img
                  src={tampon.url}
                  alt={tampon.nom}
                  style={{ maxHeight: 48, maxWidth: 80 }}
                />
                {renommageUuid === tampon.uuid ? (
                  <>
                    <input
                      type="text"
                      className="form-control"
                      style={{ maxWidth: 220 }}
                      aria-label="Nouveau nom du tampon"
                      value={renommageNom}
                      maxLength={NOM_MAX}
                      onChange={(e) => setRenommageNom(e.target.value)}
                      disabled={isRenaming}
                    />
                    {/* Un tampon hérité de LS-685 porte le nom de son fichier,
                        qui peut dépasser 60 caractères : sans ce disabled, le
                        clic serait silencieusement sans effet. */}
                    <button
                      type="button"
                      className="btn btn-default mb-0"
                      onClick={validerRenommage}
                      disabled={
                        isRenaming ||
                        renommageNom.trim() === "" ||
                        renommageNom.trim().length > NOM_MAX
                      }
                      title={
                        renommageNom.trim().length > NOM_MAX
                          ? `${NOM_MAX} caractères maximum.`
                          : undefined
                      }
                    >
                      Enregistrer
                    </button>
                    <button
                      type="button"
                      className="btn btn-default mb-0"
                      onClick={annulerRenommage}
                      disabled={isRenaming}
                    >
                      Annuler
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1 }}>{tampon.nom}</span>
                    <button
                      type="button"
                      className="btn btn-default mb-0"
                      onClick={() => ouvrirRenommage(tampon)}
                    >
                      <i className="fa fa-pencil" style={{ marginRight: 4 }} />
                      Renommer
                    </button>
                    <button
                      type="button"
                      className="btn btn-default text-danger mb-0"
                      onClick={() => setSuppressionCible(tampon)}
                    >
                      <i className="fa fa-trash" style={{ marginRight: 4 }} />
                      Supprimer
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <input
            type="text"
            className="form-control"
            style={{ maxWidth: 220 }}
            placeholder="Nom du tampon"
            aria-label="Nom du tampon"
            value={nom}
            maxLength={NOM_MAX}
            onChange={(e) => setNom(e.target.value)}
            disabled={isUploading}
          />
          <label
            className={`btn btn-default mb-0 ${nomValide ? "" : "disabled"}`}
            style={{ marginBottom: 0 }}
          >
            <i
              className={`fa fa-${isUploading ? "spinner fa-spin" : "upload"}`}
              style={{ marginRight: 4 }}
            />
            {isUploading ? "Envoi..." : "Déposer un tampon"}
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFileChange}
              disabled={isUploading || !nomValide}
              hidden
            />
          </label>
        </div>
        <p className="text-muted mb-0" style={{ marginTop: 4 }}>
          Nommez le tampon avant de choisir le fichier — PNG ou JPEG, 5 Mo max.
        </p>
      </div>

      <ConfirmModal
        isOpen={suppressionCible !== null}
        title="Supprimer le tampon"
        message={`Voulez-vous vraiment supprimer le tampon « ${
          suppressionCible?.nom || ""
        } » ?`}
        confirmLabel="Supprimer"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setSuppressionCible(null)}
      />
    </div>
  );
};

export default TamponsManager;
